import { DiscussionContextType, QUERY_KEYS } from "@cnx/common";
import { Modal } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import theme from "common/theme";
import { ChatFeature } from "components/Chat/ChatFeature";
import ChatHeaderPanel from "components/Chat/ui/ChatHeaderPanel";
import ChatContextPopover from "components/ChatContextPopover";
import ChatParticipants from "components/ChatParticipants/ChatParticipants";
import ChatRooms from "components/ChatRooms";
import { useChatAvailability } from "hooks/useChatAvailability";
import { useChatDiscussionContext } from "hooks/useChatDiscussionContext";
import { useChatListenersSocket } from "hooks/useChatListenersSocket";
import { useChatPanelInner } from "hooks/useChatPanelInner";
import { useDiscussion } from "hooks/useDiscussions";
import { useState } from "react";
import { useChatPanelStore } from "stores/chatPanel";

const ChatPanelInner = ({
  discussionId,
  contextId,
  context,
  onGoBack,
  onClose,
  onViewParticipants,
  scrollMode,
}: {
  discussionId?: string | null;
  contextId?: string | null;
  context?: DiscussionContextType | null;
  onGoBack?: () => void;
  onClose: () => void;
  onViewParticipants: () => void;
  scrollMode?: "window" | "container";
}) => {
  const { discussion } = useDiscussion({
    discussionId: discussionId ?? undefined,
  });
  const { typingLabel } = useChatListenersSocket({
    discussionId: discussionId ?? undefined,
  });
  const { discussionContext } = useChatDiscussionContext({
    contextId: contextId ?? undefined,
    context: context ?? discussion?.context ?? undefined,
  });

  const {
    title,
    stopActivityKind,
    discussionType,
    showHeader,
    handleDiscussionCreated,
  } = useChatPanelInner({
    discussionId,
    contextId,
    context,
    discussion,
    discussionContext,
  });

  return (
    <ChatFeature
      key={contextId ?? discussionId ?? undefined}
      discussionId={discussionId ?? undefined}
      context={context ?? undefined}
      contextId={contextId ?? undefined}
      scrollMode={scrollMode}
      onGoBack={onGoBack}
      onDiscussionCreated={!discussionId ? handleDiscussionCreated : undefined}
      header={
        showHeader ? (
          <ChatHeaderPanel
            discussionId={discussionId ?? undefined}
            discussionType={discussionType}
            phone={discussion?.phone}
            avatarId={discussion?.image}
            participantName={discussion?.participantName}
            context={discussion?.context ?? context ?? undefined}
            stopActivityKind={stopActivityKind ?? undefined}
            discussionContext={discussionContext}
            typingLabel={typingLabel}
            onGoBack={onGoBack}
            onClose={onClose}
            onViewParticipants={onViewParticipants}
            title={title ?? ""}
            className="border-b border-gray"
          />
        ) : null
      }
      editHeader={
        showHeader
          ? onCancelEdit => (
              <ChatHeaderPanel
                discussionId={discussionId ?? undefined}
                discussionType={discussionType}
                phone={discussion?.phone}
                avatarId={discussion?.image}
                participantName={discussion?.participantName}
                context={discussion?.context ?? context ?? undefined}
                stopActivityKind={stopActivityKind ?? undefined}
                discussionContext={discussionContext}
                typingLabel={typingLabel}
                onGoBack={onCancelEdit}
                title={title ?? ""}
                className="border-b border-gray"
              />
            )
          : undefined
      }
      popover={
        discussionContext ? (
          <ChatContextPopover discussionContext={discussionContext} />
        ) : null
      }
    />
  );
};

const ChatPanelContent = ({
  panelDiscussionId,
  panelContextId,
  panelContext,
  onGoBack,
  onClose,
  onSelect,
  scrollMode,
  isDesktop,
}: {
  panelDiscussionId: string | null;
  panelContextId?: string | null;
  panelContext?: DiscussionContextType | null;
  onGoBack?: () => void;
  onClose: () => void;
  onSelect: (discussionId: string) => void;
  scrollMode?: "window" | "container";
  isDesktop?: boolean;
}) => {
  const [showParticipants, setShowParticipants] = useState(false);

  if (panelDiscussionId && showParticipants) {
    return (
      <ChatParticipants
        discussionId={panelDiscussionId}
        onClose={() => setShowParticipants(false)}
        onClosePanel={onClose}
      />
    );
  }

  if (panelDiscussionId || (panelContext && panelContextId)) {
    const innerPanel = (
      <ChatPanelInner
        key={panelContextId ?? panelDiscussionId}
        discussionId={panelDiscussionId}
        contextId={panelContextId}
        context={panelContext}
        onGoBack={onGoBack}
        onClose={onClose}
        onViewParticipants={() => setShowParticipants(true)}
        scrollMode={scrollMode}
      />
    );
    if (scrollMode === "window") return innerPanel;
    return (
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        {innerPanel}
      </div>
    );
  }

  if (scrollMode === "window") {
    return (
      <ChatRooms onSelect={onSelect} onClose={onClose} isDesktop={isDesktop} />
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <ChatRooms onSelect={onSelect} onClose={onClose} isDesktop={isDesktop} />
    </div>
  );
};

export const ChatPanel = () => {
  const isPanelOpen = useChatPanelStore(store => store.isPanelOpen);
  const panelDiscussionId = useChatPanelStore(store => store.panelDiscussionId);
  const panelContextId = useChatPanelStore(store => store.panelContextId);
  const panelContext = useChatPanelStore(store => store.panelContext);
  const setIsPanelOpen = useChatPanelStore(store => store.setIsPanelOpen);
  const setPanelDiscussionId = useChatPanelStore(
    store => store.setPanelDiscussionId
  );
  const canGoBack = useChatPanelStore(store => store.canGoBack);
  const setCanGoBack = useChatPanelStore(store => store.setCanGoBack);
  const queryClient = useQueryClient();
  const isChatAvailable = useChatAvailability();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints?.lg})`);

  if (!isChatAvailable) return null;
  if (isDesktop === undefined) return null;

  const handleClose = () => {
    setIsPanelOpen(false);
    setPanelDiscussionId(null);
  };

  const handleSelect = (discussionId: string, contextId?: string | null) => {
    queryClient.removeQueries({
      queryKey: [QUERY_KEYS.MESSAGES, discussionId],
    });
    setCanGoBack(true);
    setPanelDiscussionId(discussionId, contextId);
  };

  const handleGoBack = () => setPanelDiscussionId(null);

  if (isDesktop) {
    if (!isPanelOpen) return null;

    return (
      <div
        className="fixed right-0 bottom-0 w-[480px] flex flex-col bg-gray-lightest border-l border-gray overflow-hidden z-20 shadow-lg"
        style={{ top: "var(--header-height, 60px)" }}
      >
        <ChatPanelContent
          panelDiscussionId={panelDiscussionId}
          panelContextId={panelContextId}
          panelContext={panelContext}
          onGoBack={handleGoBack}
          onClose={handleClose}
          onSelect={handleSelect}
          scrollMode="container"
          isDesktop
        />
      </div>
    );
  }

  return (
    <Modal
      opened={isPanelOpen}
      onClose={handleClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      zIndex={300}
      styles={{
        body: { padding: 0 },
        content: { backgroundColor: "var(--color-gray-lightest)" },
      }}
    >
      <ChatPanelContent
        panelDiscussionId={panelDiscussionId}
        panelContextId={panelContextId}
        panelContext={panelContext}
        onGoBack={canGoBack ? handleGoBack : undefined}
        onClose={handleClose}
        onSelect={handleSelect}
        scrollMode="window"
      />
    </Modal>
  );
};
